/*
 * Trafford shared cart helper.
 *
 * Routes the custom PDP add-to-cart flows (watch hero, strap/merch hero) and the
 * in-cart upsell through Horizon's OWN native cart pipeline, so adding an item
 * opens the drawer and re-renders it in place instead of hard-redirecting to /cart.
 *
 * It mirrors what assets/product-form.js does:
 *   1. collect the section ids of every <cart-items-component> on the page,
 *   2. POST the items to Theme.routes.cart_add_url with a `sections` param,
 *   3. dispatch a CartAddEvent carrying response.sections.
 * cart-drawer-component (auto-open) then opens, and cart-items-component morphs
 * its own section from event.detail.data.sections (see component-cart-items.js).
 *
 * Exposed both as an ES module export AND on window.traffordAddToCart, so the
 * existing ES5 IIFE PDP scripts can call it without being rewritten as modules.
 */
import { CartAddEvent } from '@theme/events';

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

/**
 * Add one or more line items via the AJAX cart, then open/refresh the drawer.
 * @param {Array<{id:(string|number), quantity?:number, properties?:Object}>} items
 * @param {{sourceId?:string, onError?:function}} [opts]
 * @returns {Promise<Object>} the /cart/add.js response
 */
export async function traffordAddToCart(items, opts = {}) {
  const payloadItems = (items || [])
    .filter((it) => it && it.id)
    .map((it) => ({ quantity: 1, ...it }));
  if (!payloadItems.length) return null;

  const sections = cartSectionIds();

  const res = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: payloadItems,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    }),
  });
  const data = await res.json();

  // Shopify returns { status, message, description } on failure (e.g. sold out).
  // Do NOT dispatch a CartAddEvent here: that would auto-open the drawer on a
  // failed add (the empty-drawer confusion). Just surface the error.
  if (data && data.status) {
    if (typeof opts.onError === 'function') opts.onError(data);
    throw new Error(data.description || data.message || 'Cart error');
  }

  // Fetch the fresh cart so the bubble / a11y announcer get an accurate count.
  let cart;
  try {
    cart = await (
      await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
    ).json();
  } catch (e) {
    cart = undefined;
  }

  document.dispatchEvent(
    new CartAddEvent(cart, opts.sourceId || 'trafford-cart', {
      source: 'trafford-cart',
      itemCount: cart ? cart.item_count : undefined,
      sections: data.sections,
    })
  );

  return data;
}

// Bridge for the ES5 IIFE PDP scripts + inline upsell buttons.
window.traffordAddToCart = traffordAddToCart;

// Delegated handler for the product-card "add to cart" buttons (single-variant
// products only; variant products link to the PDP instead). Adds via AJAX and
// opens the drawer. If the add fails, fall back to the PDP so the shopper is
// never stuck. Delegation on document covers cards rendered in any section.
document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-twc-card-add-form]');
  if (!form) return;
  e.preventDefault();
  const btn = form.querySelector('[type="submit"]');
  const vid = (form.querySelector('[name="id"]') || {}).value;
  if (!vid) {
    window.location = form.getAttribute('data-product-url') || Theme.routes.cart_url;
    return;
  }
  if (btn) {
    btn.disabled = true;
    btn.classList.add('is-loading');
  }
  traffordAddToCart([{ id: vid, quantity: 1 }], { sourceId: 'trafford-card' })
    .catch(() => {
      window.location = form.getAttribute('data-product-url') || Theme.routes.cart_url;
    })
    .finally(() => {
      if (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    });
});

// Delegated handler for the in-drawer upsell "Add" buttons. Delegation on
// document survives the drawer re-rendering (morph) after each cart change.
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-twc-upsell-add]');
  if (!btn) return;
  const vid = btn.getAttribute('data-variant-id');
  const kind = btn.getAttribute('data-addon-kind') || 'addon';
  if (!vid) return;
  btn.disabled = true;
  traffordAddToCart([{ id: vid, quantity: 1, properties: { _addon: kind } }], { sourceId: 'twc-upsell' })
    .catch(() => {})
    .finally(() => {
      btn.disabled = false;
    });
});
